import React, { useEffect } from 'react';

interface HowItWorksProps {
  onBack: () => void;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ onBack }) => {
  useEffect(() => {
    console.log("HowItWorks component mounted");
  }, []);

  return (
    <div className="content-card how-it-works">
      <div className="form-header">
        <div className="back-button-container">
          <button 
            type="button" 
            className="back-button"
            onClick={onBack}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor" 
              className="back-button-icon"
            >
              <path 
                fillRule="evenodd" 
                d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" 
                clipRule="evenodd" 
              />
            </svg>
            Back
          </button>
        </div>
        <h2 className="form-title">How Reverb Works</h2>
        <p className="form-subtitle">Turn your long-form content into platform-optimized posts in seconds</p>
      </div>

      <div className="how-it-works-content">
        <section className="problem-section">
          <h3 className="section-title">The Challenge</h3>
          <div className="problem-card">
            <p>
              "I create interviews and videos that perform well on YouTube, Instagram, and TikTok, 
              but I struggle to repurpose this content for text-based platforms like LinkedIn and Twitter/X."
            </p>
            <p>
              "Each platform demands its own content format - LinkedIn needs those spaced-out, 
              one-sentence-per-line posts that perform well in their algorithm. Twitter/X requires 
              an eye-catching hook, compelling visuals, and a carefully crafted thread that builds 
              to a clear call-to-action."
            </p>
            <p>
              "Just thinking about manually reformatting each video into optimized posts for 
              multiple platforms is exhausting and time-consuming. I'm missing opportunities to 
              maximize my content's reach because I can't efficiently repurpose it."
            </p>
          </div>
        </section>

        <section className="solution-section">
          <h3 className="section-title">Our Solution</h3>
          
          <div className="step-container">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4 className="step-title">Upload Your Content</h4>
              <p className="step-description">
                Simply paste a YouTube URL or upload your video/audio file. Reverb automatically 
                transcribes your content, capturing every word and nuance.
              </p>
            </div>
          </div>
          
          <div className="step-container">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4 className="step-title">Analyze Your Style</h4>
              <p className="step-description">
                Provide samples of your writing style. Reverb's AI analyzes your unique voice, 
                tone, and style patterns to ensure the generated content authentically sounds like you.
              </p>
            </div>
          </div>
          
          <div className="step-container">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4 className="step-title">Generate Platform-Optimized Content</h4>
              <p className="step-description">
                Reverb transforms your long-form content into platform-specific posts:
              </p>
              <ul className="platform-list">
                <li><strong>Twitter/X:</strong> Eye-catching hooks, perfectly paced threads, and strategic calls-to-action that drive engagement</li>
                <li><strong>LinkedIn:</strong> Professional tone with the signature one-sentence-per-line format that performs well in the algorithm</li>
                <li><strong>Reddit:</strong> Conversational posts with the right level of detail and questions that spark meaningful discussion</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="benefits-section">
          <h3 className="section-title">Why Choose Reverb?</h3>
          
          <div className="benefits-grid">
            <div className="benefit-card">
              <h4 className="benefit-title">Reclaim Your Time</h4>
              <p>Stop spending hours reformatting content. Transform a 30-minute video into platform-optimized posts in seconds.</p>
            </div>
            
            <div className="benefit-card">
              <h4 className="benefit-title">Authentic Voice</h4>
              <p>Every post sounds genuinely like you, maintaining your unique style and personality across all platforms.</p>
            </div>
            
            <div className="benefit-card">
              <h4 className="benefit-title">Platform Optimization</h4>
              <p>Each post is specifically tailored to perform well on its target platform's algorithm and audience expectations.</p>
            </div>
            
            <div className="benefit-card">
              <h4 className="benefit-title">Content Amplification</h4>
              <p>Maximize the impact of your existing content by effectively repurposing it across multiple platforms.</p>
            </div>
          </div>
        </section>
        
        <div className="cta-container">
          <button 
            className="cta-button"
            onClick={onBack}
          >
            Try Reverb Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;






